import { useState, useEffect } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Crown } from "lucide-react";
import PlayerAvatar from "../../components/football/PlayerAvatar";
import { useFb, useCountUp, lastName } from "../../utils/football";

const StatBox = ({ label, value, gold }) => (
  <div className="flex-1 rounded-xl border border-border bg-card p-3 text-center">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={`text-lg font-extrabold ${gold ? "wc-gold" : ""}`}>{value}</div>
  </div>
);

const PlayerRow = ({ slot }) => {
  const badge = slot.is_captain ? "C" : slot.is_vice_captain ? "VC" : null;
  return (
    <div className="flex items-center gap-3 py-2.5" data-testid={`result-player-${slot.player_id}`}>
      <PlayerAvatar player={slot} size={40} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold">{lastName(slot.name)} {badge && <span className="ml-1 rounded bg-amber-400/20 px-1.5 text-[10px] font-extrabold wc-gold">{badge}</span>}</div>
        <div className="text-xs text-muted-foreground">{slot.position} · {slot.team_name}</div>
      </div>
      <div className="text-right">
        <div className="text-sm font-extrabold wc-gold">{slot.points_earned ?? 0}</div>
        {slot.multiplier > 1 && <div className="text-[10px] text-muted-foreground">{slot.multiplier}x</div>}
      </div>
    </div>
  );
};

export default function FootballMatchDayResultsPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { matchDay } = useParams();
  const [squad, setSquad] = useState(null);
  const [rankToday, setRankToday] = useState(null);
  const [me, setMe] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setSquad(await fb.get(`/football/squad/${matchDay}`)); } catch {}
      try { const lb = await fb.get(`/football/leaderboard/match-day/${matchDay}`); setRankToday(lb.entries?.find((e) => e.is_current_user)?.rank || null); } catch {}
      try { setMe(await fb.get("/football/leaderboard/me")); } catch {}
      try { const p = await fb.get(`/football/predictor/${matchDay}`); setPredictions((p.questions || []).filter((q) => q.resolved && q.user_answer)); } catch {}
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDay]);

  const total = squad?.total_points || 0;
  const animated = useCountUp(total, 1500);
  const motd = squad?.manager_of_day;
  const motdBonus = motd ? 5 : 0;

  if (loading) return <div className="football-theme min-h-screen bg-background p-6"><div className="h-60 animate-pulse rounded-2xl bg-secondary/50" /></div>;
  if (!squad) return (
    <div className="football-theme min-h-screen bg-background p-6 text-center">
      <button onClick={() => navigate("/")} className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground"><ChevronLeft size={18} /> Home</button>
      <p className="mt-10 text-muted-foreground" data-testid="results-empty">No squad for this match day.</p>
    </div>
  );

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="results-page">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      <div className="wc-hero-gradient px-4 pb-6 pt-4 text-center">
        <button onClick={() => navigate("/")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="results-back"><ChevronLeft size={18} /> Home</button>
        {motd && <div className="crown-float mx-auto mb-1 inline-flex items-center gap-1.5 rounded-full wc-bg-gold px-3 py-1 text-sm font-extrabold text-slate-900" data-testid="results-motd"><Crown size={15} /> Manager of the Day</div>}
        <div className="text-sm text-white/70">Total Points</div>
        <div className="text-5xl font-extrabold text-white" data-testid="results-total">{Math.round(animated)}</div>
      </div>

      <div className="mx-auto max-w-md px-4">
        <div className="mt-4 flex gap-2">
          <StatBox label="Fantasy" value={squad.fantasy_points || 0} />
          <StatBox label="Predictor" value={squad.predictor_points || 0} />
          {motd && <StatBox label="MotD Bonus" value={`+${motdBonus}`} gold />}
        </div>
        <div className="mt-3 flex gap-2">
          <StatBox label="Rank Today" value={rankToday ? `#${rankToday}` : "—"} />
          <StatBox label="Overall Rank" value={me?.rank ? `#${me.rank}` : "—"} />
        </div>

        <div className="mt-5 rounded-xl border border-border bg-card p-4">
          <h2 className="mb-1 text-base font-bold">Player Breakdown</h2>
          <div className="divide-y divide-border">
            {(squad.squad || []).map((s) => <PlayerRow key={s.player_id} slot={s} />)}
          </div>
        </div>

        {predictions.length > 0 && (
          <div className="mt-5 rounded-xl border border-border bg-card p-4" data-testid="results-predictions">
            <h2 className="mb-2 text-base font-bold">Predictor Results</h2>
            <div className="space-y-2">
              {predictions.map((q) => (
                <div key={q.id} className="flex items-center justify-between text-sm">
                  <span className="truncate pr-2">{q.question_text}</span>
                  <span className={`font-bold ${q.user_points > 0 ? "text-emerald-400" : "text-red-400"}`}>{q.user_points > 0 ? `+${q.user_points}` : "0"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
